import { lazy, Suspense, useEffect, useReducer } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { adaBelumDisimpan } from '../app/store/belumDisimpan'
import { useJaga } from '../app/store/jaga'
import { useSesi } from '../app/store/sesi'
import { useUi } from '../app/store/ui'
import { denganKueri, kueriDariKeadaan } from '../components/primitives/useKueriJeda'
import { bawaPilihan } from '../routes/aksi-massal/pilihanRiwayat'
import type { KeadaanKunci } from '../routes/masuk/kunciMasuk'
import { arahLangkahAkun } from './arahLangkah'

/** How often a step waiting on unsaved work asks a field-by-field screen's check again. */
const TANYA_LAGI_MS = 1000

/**
 * The gate in front of the shell: no `sesi_lokal` row sends the person to /masuk, a
 * locked session to /masuk/pin (remembering where they were), an unfinished first
 * install to the snapshot screen, a session the API refused for an account step to
 * that step (./arahLangkah). A pathless layout element rather than a loader because the
 * lock changes at runtime. The idle lock that sets `terkunci` runs for the whole tab
 * (./kunciDiamTab), so the sign-in screens an open session reaches lock as well.
 */
export function AuthGuard() {
  const status = useSesi((s) => s.status)
  const instalSelesai = useSesi((s) => s.instalSelesai)
  const menunggu = useSesi((s) => s.menunggu)
  const langkahAkun = useSesi((s) => s.langkahAkun)
  const pengguna = useSesi((s) => s.sesi.userId)
  // Anything unsaved (app/store/belumDisimpan). A form's changes render the guard again through
  // the count; a field-by-field screen's check (a refused figure) changes without telling, so it
  // is asked at each render, when a screen registers one, and every second while a step waits.
  useUi((s) => s.dirtyForms > 0)
  useJaga((s) => s.periksa)
  const belumDisimpan = adaBelumDisimpan()
  const [, tanyaLagi] = useReducer((n: number) => n + 1, 0)
  const langkahMenunggu = langkahAkun !== null && belumDisimpan
  useEffect(() => {
    if (!langkahMenunggu) return
    const jam = setInterval(tanyaLagi, TANYA_LAGI_MS)
    return () => clearInterval(jam)
  }, [langkahMenunggu])
  const location = useLocation()

  if (status === 'memuat') return null
  if (status === 'tanpa_sesi') {
    return <Navigate to={menunggu === null ? '/masuk' : '/masuk/buat-pin'} replace />
  }
  if (status === 'terkunci') {
    // The lock takes the entry's place: a list's search, kept in the entry and never in the
    // address (useKueriRiwayat), and a selection sheet's ticked rows (aksi massal) go with it
    // and come back on unlocking (a reload locks). Both say whose they are: on a shared device
    // the PIN screen hands neither to another person (routes/masuk/PinRoute), and another
    // person's search or ticked rows are never taken along.
    return (
      <Navigate
        to="/masuk/pin"
        replace
        state={
          {
            dari: `${location.pathname}${location.search}`,
            keadaanDari: bawaPilihan(
              location.state,
              denganKueri(null, kueriDariKeadaan(location.state, pengguna), pengguna),
              pengguna,
            ),
            untuk: pengguna,
          } satisfies KeadaanKunci
        }
      />
    )
  }
  if (!instalSelesai) return <Navigate to="/masuk/unduhan" replace />
  // Spec 2026-09-30 akun, 6: a session the API refused for a step is led to that step (an
  // account signed in before it needed the app enrols here: every role since 2026-10-02) and
  // comes back to where it was; a form
  // with unsaved changes, or a figure not saved yet, holds it back until it is saved or left
  // (plan 6 ruling).
  const langkah = arahLangkahAkun(langkahAkun, belumDisimpan)
  if (langkah !== null) {
    // Whose step it is goes with it: locked there, the PIN hands `dari` to them only.
    return (
      <Navigate
        to={langkah}
        replace
        state={{ dari: `${location.pathname}${location.search}`, untuk: pengguna }}
      />
    )
  }
  return (
    <>
      <Outlet />
      <PenghapusanDialog />
    </>
  )
}

const PenghapusanDialogIsi = lazy(() =>
  import('./PenghapusanDialog').then((m) => ({ default: m.PenghapusanDialog })),
)

/**
 * The wipe reason (spec 15.2). Only a revoked device or account ever sees it, so the dialog
 * and its Base UI code load on demand instead of with every launch.
 */
export function PenghapusanDialog() {
  const alasan = useSesi((s) => s.penghapusan)
  if (alasan === null) return null
  return (
    <Suspense fallback={null}>
      <PenghapusanDialogIsi />
    </Suspense>
  )
}
